<div class="container-fluid p-3">
  @if (partner) {
    <form #partnerForm="ngForm" (ngSubmit)="onSave()" novalidate>
      <div class="row">
        <div class="col-12 col-xl-6 mb-3">
          <div class="card">
            <div class="card-body">
              <h5 class="card-title mb-3">Provisioning</h5>

              <div class="mb-3">
                <mat-slide-toggle
                  name="can_provision_accounts"
                  [(ngModel)]="form.can_provision_accounts"
                  (change)="onToggleProvisioning($event.checked)">
                  Allow this user to provision client accounts (partner)
                </mat-slide-toggle>
              </div>

              <div class="row">
                <div class="col">
                  <mat-form-field class="full-width mb-4" appearance="outline" subscriptSizing="dynamic">
                    <mat-label>Partner role name</mat-label>
                    <input
                      matInput
                      name="partner_role_name"
                      #roleNameModel="ngModel"
                      [(ngModel)]="form.partner_role_name"
                      maxlength="100"
                      [required]="form.can_provision_accounts"
                      id="partner_role_name">
                    <mat-hint>Shown as the role name in every client account this partner manages.</mat-hint>
                    @if (roleNameModel.errors?.required) {
                      <mat-error>Please enter a partner role name.</mat-error>
                    }
                    @if (roleNameModel.errors?.maxlength) {
                      <mat-error>The partner role name may not be longer than 100 characters.</mat-error>
                    }
                    @if (roleNameModel.errors?.api) {
                      <mat-error>{{ apiErrors['partner_role_name'] }}</mat-error>
                    }
                  </mat-form-field>
                </div>
                <div class="col">
                  <mat-form-field class="full-width mb-4" appearance="outline" subscriptSizing="dynamic">
                    <mat-label>Partner's own account ID</mat-label>
                    <input
                      matInput
                      type="number"
                      min="1"
                      name="partner_account_id"
                      #ownAccountModel="ngModel"
                      [(ngModel)]="form.partner_account_id"
                      [required]="form.can_provision_accounts"
                      id="partner_account_id">
                    <mat-hint>The partner's own company. It stays Administrators here and never gets the partner role.</mat-hint>
                    @if (ownAccountModel.errors?.required) {
                      <mat-error>Please enter the partner's own account ID.</mat-error>
                    }
                    @if (ownAccountModel.errors?.min) {
                      <mat-error>The account ID must be at least 1.</mat-error>
                    }
                    @if (ownAccountModel.errors?.api) {
                      <mat-error>{{ apiErrors['partner_account_id'] }}</mat-error>
                    }
                  </mat-form-field>
                </div>
              </div>

              <div class="mt-3">
                <h6 class="mb-1">Own accounts</h6>
                @if (partner.own_accounts?.length) {
                  <ul class="mb-2 pl-3">
                    @for (ownAccount of partner.own_accounts; track ownAccount.id) {
                      <li>
                        {{ ownAccount.name }} (#{{ ownAccount.id }})
                        <span class="text-muted">{{ getOwnAccountKindLabel(ownAccount.kind) }}</span>
                      </li>
                    }
                  </ul>
                } @else {
                  <p class="text-muted mb-2">None.</p>
                }
              </div>

              @if (employerMismatch) {
                <div class="alert alert-warning" role="alert">
                  This user's employer ({{ partner.employer_account.name }}, #{{ partner.employer_account.id }}) is not the pinned own account. Check the own account before saving.
                </div>
              }

              <p class="text-muted mb-0">
                Linked accounts: {{ partner.linked_accounts_count }}. Partner roles: {{ partner.partner_roles_count }}.
              </p>
            </div>
          </div>
        </div>

        <div class="col-12 col-xl-6 mb-3">
          <div class="card">
            <div class="card-body">
              <h5 class="card-title mb-3">Permission template</h5>
              <p class="mb-2">{{ form.rbac_permission_ids.length }} permissions selected</p>
              <div class="mb-3">
                <button type="button" mat-stroked-button class="mr-2 mb-1" (click)="onEditPermissions()" matTooltip="Choose the permissions the partner role gets in every client account.">
                  <mat-icon>edit</mat-icon> Edit Permissions
                </button>
                <button type="button" mat-stroked-button class="mb-1" (click)="onResetToDefault()" matTooltip="Replace the selection with the default partner permissions.">
                  <mat-icon>restore</mat-icon> Reset to Default
                </button>
              </div>
              <p class="text-muted small mb-0">
                Edit and delete include view automatically when the feature has a view permission. The template is applied in full in every client account; the account's plan decides what is usable.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-end mb-3">
        @if (saved.can_provision_accounts) {
          <span [matTooltip]="isDirty ? 'Save your changes first.' : ''">
            <button
              type="button"
              mat-stroked-button
              class="mr-2"
              [disabled]="isDirty || runActive || inProgress"
              (click)="onResync()">
              <mat-icon>sync</mat-icon> Resync
            </button>
          </span>
        }
        <button type="submit" mat-raised-button color="primary" [disabled]="inProgress">
          <mat-icon>save</mat-icon> Save
        </button>
      </div>
    </form>

    @if (partner.last_run; as run) {
      <div class="card">
        <div class="card-body">
          <h5 class="card-title mb-3">Last sync</h5>

          <div class="row mb-3">
            <div class="col-12 col-md-6">
              <p class="mb-1">
                Status:
                <span class="badge badge-pill" [ngClass]="{
                  'badge-secondary': run.status === 'queued',
                  'badge-info': run.status === 'running',
                  'badge-success': run.status === 'completed',
                  'badge-danger': run.status === 'failed'
                }">{{ run.status | titlecase }}</span>
                @if (run.summary?.totals?.stopped_reason === 'flag_off') {
                  <span class="ml-2 text-muted">(stopped because provisioning was turned off)</span>
                }
              </p>
              <p class="mb-1">Triggers: {{ run.triggers?.join(', ') }}</p>
              <p class="mb-1">Source: {{ run.source }}</p>
            </div>
            <div class="col-12 col-md-6">
              <p class="mb-1">Queued: {{ run.queued_at * 1000 | timezoneConvertMedium }}</p>
              <p class="mb-1">
                Started:
                @if (run.started_at) {
                  {{ run.started_at * 1000 | timezoneConvertMedium }}
                } @else {
                  <span class="text-muted">Not yet</span>
                }
              </p>
              <p class="mb-1">
                Finished:
                @if (run.finished_at) {
                  {{ run.finished_at * 1000 | timezoneConvertMedium }}
                } @else {
                  <span class="text-muted">Not yet</span>
                }
              </p>
            </div>
          </div>

          @if (run.status === 'failed' && run.error) {
            <div class="alert alert-danger" role="alert">
              {{ run.error }}
            </div>
          }

          @if (totalsRows.length > 0) {
            <table class="table table-sm table-striped mb-3">
              <tbody>
                @for (row of totalsRows; track row.label) {
                  <tr>
                    <th scope="row" class="font-weight-normal">{{ row.label }}</th>
                    <td class="text-right">{{ row.value }}</td>
                  </tr>
                }
              </tbody>
            </table>
          }

          @if (run.summary?.accounts?.length) {
            <h6>Accounts</h6>
            <div class="table-responsive">
              <table class="table table-sm table-bordered mb-0">
                <thead>
                  <tr>
                    <th scope="col">Account ID</th>
                    <th scope="col">Name</th>
                    <th scope="col">Status</th>
                    <th scope="col">Skip reason</th>
                    <th scope="col">Other roles</th>
                    <th scope="col">Error</th>
                  </tr>
                </thead>
                <tbody>
                  @for (account of run.summary.accounts; track $index) {
                    <tr>
                      <td>{{ account.account_id }}</td>
                      <td>{{ account.account_name }}</td>
                      <td>{{ account.status }}</td>
                      <td>{{ account.skip_reason }}</td>
                      <td>{{ getOtherRoles(account) }}</td>
                      <td>{{ account.error }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </div>
      </div>
    }
  }
</div>
